import { Link } from 'react-router';
import { 
  GuideLayout, 
  GuideSection, 
  GuideTip, 
  GuideSteps,
  GuideStep,
  GuideGrid,
  GuideCard,
  GuideDarkSection
} from '@/components/guides/guide-layout';

const inlineLink =
  'text-brand-600 hover:text-brand-700 font-medium underline decoration-brand-300 underline-offset-2';

export function PhotographingPotholeDamageGuide() {
  return (
    <GuideLayout
      slug="photographing-pothole-damage"
      category="Evidence"
      categoryColor="accent"
      title="How to Photograph Pothole Damage"
      description="Take photos that clearly show the defect and support your claim. Good evidence photography can make the difference between success and rejection."
      readTime="6 min"
      updatedDate="12 January 2026"
      reviewedFor="UK claims practice"
      relatedGuides={[
        { slug: 'documenting-vehicle-damage', title: 'Documenting Your Vehicle Damage', readTime: '5 min' },
        { slug: 'what-makes-a-valid-claim', title: 'What Makes a Valid Pothole Claim?', readTime: '5 min' },
        { slug: 'getting-repair-quotes', title: 'Getting the Right Repair Quote', readTime: '4 min' },
      ]}
      atAGlance={[
        { label: 'Four essential shots', value: 'Close-up, wide shot, measurement shot, and location shot' },
        { label: 'Showing depth', value: 'Place a ruler — or a £1 coin, pen, or phone — in the deepest part and photograph side-on at ground level' },
        { label: 'Council intervention level', value: 'Around 40mm (about 1.5 inches) deep' },
        { label: 'Best timing', value: 'Same day as the incident; otherwise return within 24-48 hours' },
        { label: 'If already repaired', value: 'Photograph the fresh repair patch — it proves the defect existed' },
      ]}
      howTo={{
        name: 'How to Photograph Pothole Damage for a Claim',
        description:
          'Six steps to capture evidence photos of a pothole that clearly show its depth, position, and exact location.',
        steps: [
          {
            name: 'Get there safely',
            text: "Never take risks to photograph a pothole. On busy roads, wait for a safe gap or return at a quieter time, and use the phone's zoom for distant shots. On motorways, use dashcam footage rather than stopping on the hard shoulder.",
          },
          {
            name: 'Take the close-up',
            text: 'Get as close as safely possible and show the edges, cracks, and depth of the pothole. This is the primary evidence that a dangerous defect existed.',
          },
          {
            name: 'Add measurement',
            text: 'Place a ruler, tape measure, or reference object in the deepest part of the pothole. Take photos from multiple angles — side-on is best for showing depth.',
          },
          {
            name: 'Capture context',
            text: "Step back and show the pothole's position on the road, including any road markings, the carriageway edge, and surrounding features.",
          },
          {
            name: 'Document the location',
            text: 'Photograph nearby landmarks such as road signs, house numbers, or business names. Enable GPS on the phone, screenshot a dropped map pin, or record a what3words address for the precise spot.',
          },
          {
            name: 'Take multiple shots',
            text: 'Take several photos of each type rather than just one, and select the clearest images later. Better to have too many than too few.',
          },
        ],
      }}
      faqs={[
        {
          question: 'What photos do I need for a pothole claim?',
          answer: 'You need four types of photos: a close-up of the pothole showing depth and edges, a wide shot showing its position on the road, a measurement shot with a ruler or common object for scale, and location shots showing road signs or landmarks to prove where it was.',
        },
        {
          question: 'How do I show the depth of a pothole in photos?',
          answer: 'The best method is to place a ruler or tape measure vertically in the deepest part of the pothole. If you don\'t have a ruler, use a common object like a £1 coin (3mm thick), a standard pen (10mm diameter), or your phone (about 8mm thick). Photograph from the side at ground level to show depth clearly.',
        },
        {
          question: 'What if I couldn\'t photograph the pothole at the time?',
          answer: 'Return as soon as safely possible — ideally within 24-48 hours. If the pothole has been repaired, photograph the fresh repair patch as evidence the defect existed. You can also check FixMyStreet for prior reports that may include photos, or request the council\'s inspection records.',
        },
        {
          question: 'Does my phone camera work for pothole evidence photos?',
          answer: 'Yes, modern smartphone cameras are absolutely fine for evidence photos. Make sure your lens is clean, tap to focus on the pothole, and take multiple shots. Enable location services so your photos include GPS coordinates.',
        },
        {
          question: 'Is it safe to photograph potholes on busy roads?',
          answer: 'Safety comes first. Never stand in live traffic. Wait for a safe gap, wear visible clothing, or return at a quieter time. Use your phone\'s zoom for distant shots. For motorways, use dashcam footage if available rather than stopping on the hard shoulder.',
        },
        {
          question: 'How do I prove the exact location of the pothole?',
          answer: 'Use multiple methods: enable GPS on your phone so photos include coordinates, drop a pin in Google/Apple Maps and screenshot it, use what3words for a precise 3-word address, and photograph nearby landmarks like house numbers, road signs, or business names.',
        },
        {
          question: 'What if it\'s raining when I photograph the pothole?',
          answer: 'If possible, wait for the water to drain. A water-filled pothole makes it impossible to judge depth, and councils may argue the defect was shallower than claimed. If you can\'t wait, take photos anyway and note in your claim that it was raining.',
        },
        {
          question: 'Do photos need to be dated?',
          answer: 'Yes, timing matters. Most smartphones automatically embed the date and time in photo metadata. If yours doesn\'t, note the date, time, and location immediately after taking photos. Photos taken the same day as the incident are most compelling.',
        },
      ]}
    >
      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        }
        title="Why Photos Matter"
      >
        <p className="mb-4">
          Your photographs are often the <strong>most important evidence</strong> in a{' '}
          <Link to="/guides/what-makes-a-valid-claim" className={inlineLink}>
            pothole claim
          </Link>
          . They need to clearly show the defect existed, how severe it was, and where it was located. 
          Poor photos can sink an otherwise strong claim.
        </p>
        <GuideTip variant="info" title="Perfect for:">
          Anyone who has experienced{' '}
          <Link to="/guides/documenting-vehicle-damage" className={inlineLink}>
            pothole damage
          </Link>
          {' '}and needs to document the defect properly. 
          Even if you've already taken some photos, this guide will help you identify what else you need.
        </GuideTip>
      </GuideSection>

      <GuideDarkSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        }
        title="The Four Essential Shots"
      >
        <p className="text-brand-200 mb-4">
          For a strong claim, capture these four types of photograph:
        </p>
        <GuideGrid cols={2}>
          <GuideCard variant="dark" title="📷 Close-up Shot">
            Get close to show the depth, edges, and texture of the pothole. This demonstrates severity.
          </GuideCard>
          <GuideCard variant="dark" title="📷 Wide Shot">
            Step back to show the pothole in context — its position on the road, nearby features.
          </GuideCard>
          <GuideCard variant="dark" title="📷 Measurement Shot">
            Place a ruler, tape measure, or common object next to the pothole to show scale.
          </GuideCard>
          <GuideCard variant="dark" title="📷 Location Shot">
            Capture road signs, house numbers, or landmarks that prove exactly where it was.
          </GuideCard>
        </GuideGrid>
      </GuideDarkSection>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        }
        title="The Golden Rule: Show the Depth"
      >
        <p className="mb-4">
          Depth is the most critical factor councils assess. Most use an "intervention level" of 
          around <strong>40mm (about 1.5 inches)</strong> — defects shallower than this may be 
          considered acceptable.
        </p>
        
        <h4 className="font-semibold text-brand-950 mb-3">How to show depth clearly:</h4>
        <GuideGrid cols={3}>
          <GuideCard title="Best: Ruler">
            Place a ruler or tape measure vertically in the deepest part — the gold standard for evidence.
          </GuideCard>
          <GuideCard title="Good: Common Objects">
            A £1 coin (3mm), pen (10mm diameter), or phone (8mm) can work as reference.
          </GuideCard>
          <GuideCard title="Angle Matters">
            Photograph from the side at ground level — not straight down — to show true depth.
          </GuideCard>
        </GuideGrid>

        <GuideTip variant="warning" title="Water-filled potholes">
          If possible, wait for water to drain before photographing. Councils may argue a 
          water-filled pothole was shallower than claimed because you couldn't measure the depth.
        </GuideTip>
      </GuideSection>

      <GuideSteps>
        <GuideStep number={1} title="Get There Safely">
          <p className="mb-2">
            Never put yourself at risk to photograph a pothole. If it's on a busy road, wait for 
            a safe gap or return at a quieter time. Use your phone's zoom for distant shots.
          </p>
          <GuideTip variant="warning">
            For motorways, don't stop on the hard shoulder just for photos. Use dashcam footage 
            if available, and report the defect to{' '}
            <Link to="/guides/who-is-responsible" className={inlineLink}>
              National Highways
            </Link>
            .
          </GuideTip>
        </GuideStep>

        <GuideStep number={2} title="Take the Close-up">
          <p>
            Get as close as safely possible. Show the edges, cracks, and depth of the pothole. 
            This is your primary evidence that a dangerous defect existed.
          </p>
        </GuideStep>

        <GuideStep number={3} title="Add Measurement">
          <p>
            Place your ruler, tape measure, or reference object in the deepest part. Take photos 
            from multiple angles — side-on is best for showing depth.
          </p>
        </GuideStep>

        <GuideStep number={4} title="Capture Context">
          <p>
            Step back and show the pothole's position on the road. Include any road markings, 
            the carriageway edge, and surrounding features.
          </p>
        </GuideStep>

        <GuideStep number={5} title="Document Location">
          <p className="mb-2">
            Photograph nearby landmarks — road signs, house numbers, business names. Also use 
            digital methods:
          </p>
          <GuideGrid cols={3}>
            <GuideCard variant="accent" title="GPS Photos">
              Enable location on your phone so photos include coordinates
            </GuideCard>
            <GuideCard variant="accent" title="Drop a Pin">
              Screenshot a Google/Apple Maps pin at the exact spot
            </GuideCard>
            <GuideCard variant="accent" title="what3words">
              Get a unique 3-word address for the precise location
            </GuideCard>
          </GuideGrid>
        </GuideStep>

        <GuideStep number={6} title="Take Multiple Shots">
          <p>
            Don't just take one photo of each type — take several. Storage is free, and you can 
            select the clearest ones later. Better to have too many than too few.
          </p>
        </GuideStep>
      </GuideSteps>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        }
        title="Timing Matters"
      >
        <p className="mb-4">
          Take your photos <strong>as soon as possible</strong> after the incident. Here's why:
        </p>
        <GuideGrid cols={2}>
          <GuideCard title="Same Day Is Ideal">
            Photos taken immediately after the incident are the most compelling evidence.
          </GuideCard>
          <GuideCard title="Potholes Get Fixed">
            Councils may repair the defect before you return, weakening your evidence.
          </GuideCard>
          <GuideCard title="Weather Changes Things">
            Rain, frost, and traffic can alter a pothole's appearance over time.
          </GuideCard>
          <GuideCard title="Memory Fades">
            You'll remember exact details better if you document immediately.
          </GuideCard>
        </GuideGrid>
        <p className="mt-4 text-surface-700">
          If you couldn't photograph at the time (e.g., you were on a motorway), return as soon 
          as safely possible — ideally within 24-48 hours. Acting promptly also keeps the rest of your{' '}
          <Link to="/guides/claim-timeline" className={inlineLink}>
            claim timeline
          </Link>
          {' '}on track.
        </p>
      </GuideSection>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        }
        title="Technical Tips"
      >
        <p className="mb-4">Get the clearest possible images:</p>
        <ul className="space-y-3">
          <li className="flex items-start gap-3">
            <span className="text-success-500 flex-shrink-0">✓</span>
            <span><strong>Clean your lens</strong> — Smudges cause blur. Quick wipe before shooting.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-success-500 flex-shrink-0">✓</span>
            <span><strong>Good lighting</strong> — Daylight is best. Avoid harsh shadows or very low light.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-success-500 flex-shrink-0">✓</span>
            <span><strong>Tap to focus</strong> — On your smartphone, tap the pothole on screen to focus on it.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-success-500 flex-shrink-0">✓</span>
            <span><strong>Steady hands</strong> — Brace your elbows or use both hands to reduce blur.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-success-500 flex-shrink-0">✓</span>
            <span><strong>Avoid flash</strong> — Natural light shows depth better than flash, which flattens images.</span>
          </li>
        </ul>
      </GuideSection>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        }
        title="If the Pothole Has Been Repaired"
      >
        <p className="mb-4">Don't give up if you return to find the pothole fixed:</p>
        <GuideGrid cols={2}>
          <GuideCard title="Photo the Repair">
            Fresh tarmac shows where the pothole was and proves it existed.
          </GuideCard>
          <GuideCard title="Check Prior Reports">
            <Link to="/guides/prior-reports-explained" className={inlineLink}>
              FixMyStreet and council records
            </Link>
            {' '}may have photos from before the repair.
          </GuideCard>
          <GuideCard title="Dashcam Footage">
            If you have footage of the incident, this substitutes for later photos.
          </GuideCard>
          <GuideCard title="Request Records">
            Councils keep{' '}
            <Link to="/guides/section-58-defence" className={inlineLink}>
              inspection logs
            </Link>
            {' '}— the repair record proves the defect existed.
          </GuideCard>
        </GuideGrid>
        <GuideTip variant="success" title="Repairs can help your case">
          A quick repair actually supports your claim — it suggests the council knew the defect 
          was serious enough to fix promptly.
        </GuideTip>
      </GuideSection>

      <div className="bg-success-500/10 border border-success-500/30 rounded-2xl p-6 mb-8">
        <h3 className="font-display text-lg font-bold text-brand-950 mb-4 flex items-center gap-2">
          <svg className="w-5 h-5 text-success-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Photo Checklist
        </h3>
        <p className="text-surface-700 mb-4">Before you leave, make sure you have:</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="flex items-center gap-2 text-surface-700">
            <svg className="w-5 h-5 text-success-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Close-up showing depth and edges
          </div>
          <div className="flex items-center gap-2 text-surface-700">
            <svg className="w-5 h-5 text-success-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Photo with ruler/object for scale
          </div>
          <div className="flex items-center gap-2 text-surface-700">
            <svg className="w-5 h-5 text-success-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Wide shot showing road position
          </div>
          <div className="flex items-center gap-2 text-surface-700">
            <svg className="w-5 h-5 text-success-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Nearby landmarks photographed
          </div>
          <div className="flex items-center gap-2 text-surface-700">
            <svg className="w-5 h-5 text-success-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            GPS/location recorded
          </div>
          <div className="flex items-center gap-2 text-surface-700">
            <svg className="w-5 h-5 text-success-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Date and time noted
          </div>
        </div>
      </div>
      <GuideTip variant="info" title="Using dashcam footage">
        See the{' '}
        <Link to="/guides/dashcam-pothole-evidence" className={inlineLink}>
          dashcam evidence guide
        </Link>{' '}
        for preserving original files, metadata and a separate working copy.
      </GuideTip>
    </GuideLayout>
  );
}
